// Expression evaluation (INTERACTION-MODEL-V1.md §5). Expressions are a typed
// AST validated statically (validate/interaction.ts); this evaluates an
// ALREADY VALIDATED expression against a read-only environment. Pure: no
// strings are parsed, nothing is executed, randomness comes only from the
// environment (the instance RNG, never Math.random). Shared by the Studio
// preview (7.6D) and the Delivery Runtime (7.6H).
//
// Defined arithmetic: division by zero = 0; non-finite results = 0.
// Comparisons of mismatched types are rejected by the validator; at runtime
// they evaluate to false (never throw).

import type { Expr } from "./schema/document.js";

export type ExprValue = number | boolean | string;

export interface ExprEnvironment {
  readonly variable: (id: string) => ExprValue | undefined;
  readonly elementState: (id: string) => string;
  readonly currentScene: () => string;
  readonly timer: (id: string, prop: "remainingMs" | "running") => number | boolean;
  readonly fieldProp?: (form: string, field: string, prop: "filled" | "valid") => boolean;
  readonly answered?: (question: string, option?: string) => boolean;
  /** Instance RNG in [0, 1). Required only for `random` nodes (action values). */
  readonly random?: () => number;
}

const finite = (v: number) => (Number.isFinite(v) ? v : 0);
const num = (v: ExprValue) => (typeof v === "number" ? v : 0);

export function evaluateExpr(e: Expr, env: ExprEnvironment): ExprValue {
  if (typeof e === "number" || typeof e === "boolean" || typeof e === "string") return e;
  if ("var" in e) return env.variable(e.var) ?? 0;
  if ("elementState" in e) return env.elementState(e.elementState);
  if ("scene" in e) return env.currentScene();
  if ("field" in e) return env.fieldProp?.(e.form, e.field, e.prop) ?? false;
  if ("answered" in e) return env.answered?.(e.answered, e.option) ?? false;
  if ("timer" in e) return env.timer(e.timer, e.prop);
  if ("random" in e) {
    const r = env.random?.() ?? 0;
    const { min, max } = e.random;
    const v = min + r * (max - min);
    return e.random.int ? Math.min(max, Math.floor(min + r * (max - min + 1))) : finite(v);
  }
  if ("arg" in e) {
    const a = evaluateExpr(e.arg, env);
    switch (e.op) {
      case "not":
        return a !== true;
      case "round":
        return Math.round(num(a));
      case "floor":
        return Math.floor(num(a));
      case "ceil":
        return Math.ceil(num(a));
      case "abs":
        return Math.abs(num(a));
    }
  }
  const op = e.op;
  if (op === "and") return e.args.every((a) => evaluateExpr(a, env) === true);
  if (op === "or") return e.args.some((a) => evaluateExpr(a, env) === true);
  if (op === "clamp") {
    const [v, lo, hi] = e.args.map((a) => num(evaluateExpr(a, env))) as [number, number, number];
    return Math.min(Math.max(v, lo), hi);
  }
  const [l, r] = (e.args as readonly [Expr, Expr]).map((a) => evaluateExpr(a, env)) as [ExprValue, ExprValue];
  switch (op) {
    case "==":
      return l === r;
    case "!=":
      return l !== r;
    case "<":
      return typeof l === "number" && typeof r === "number" && l < r;
    case "<=":
      return typeof l === "number" && typeof r === "number" && l <= r;
    case ">":
      return typeof l === "number" && typeof r === "number" && l > r;
    case ">=":
      return typeof l === "number" && typeof r === "number" && l >= r;
    case "+":
      return finite(num(l) + num(r));
    case "-":
      return finite(num(l) - num(r));
    case "*":
      return finite(num(l) * num(r));
    case "/":
      return num(r) === 0 ? 0 : finite(num(l) / num(r));
    case "min":
      return Math.min(num(l), num(r));
    case "max":
      return Math.max(num(l), num(r));
  }
  return false;
}
